/**
 * PrescriptionProfile.tsx — the clinician's "Your profile" panel, rebuilt
 * LinkedIn-style (UX Wave 8 §1, 2026-07-21; CLINICIAN, /clinicians?tab=profile
 * — reached from the top avatar/account menu since §2 removed the rail item).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 8.
 *
 * VIEW mode: avatar photo (signed READ URL via identity; initial-letter circle
 * when none), first/last name (rendered from the derived fullName), the
 * designation line, the REGISTERED BODY + registration number (both READ-ONLY
 * — the registered body follows from `prescriberType`: doctor → GMC,
 * pharmacist → GPhC; prescribing identity is admin-managed, G2), and the
 * short bio. "Edit profile" flips the panel to EDIT mode, unlocking ALL the
 * self-serve fields inline — first name, last name, designation, bio (≤600)
 * and the photo — then Save PATCHes once and returns to view mode.
 *
 * PHOTO: choose file (jpeg/png) → on Save: grant (POST me/photo-upload, the
 * signed-PUT seam — `{stub: true}` = storage unconfigured, an info note) →
 * PUT with the grant-pinned content type → the PATCH carries `photoRef` as
 * the CONFIRM (identity stamps only after the PUT — the same dangling-ref
 * discipline as the signature). The saved photo also feeds the top account
 * menu's avatar thumbnail via the clinicians/profilePhoto store.
 *
 * SIGNATURE (Wave 8: the giant "Replace signature" button is GONE): when on
 * file the actual signature IMAGE renders inline (signed read; a stub-mode
 * "on file, preview unavailable" note when the bucket is unconfigured) with a
 * SMALL ghost "Replace" control revealing the draw-on-screen pad
 * (ui/SignaturePad); when absent the panel says "No signature on file" and
 * shows the pad directly. The save flow stays grant → PNG PUT (LITERAL
 * image/png content type — the signed URL pins it) → CONFIRM, each step
 * failing with its own message (TA-06); confirm returns the refreshed
 * profile.
 *
 * A caller with NO clinician record (e.g. the master admin account) gets a
 * 404 on the profile read — the panel hides quietly rather than erroring.
 * MONEY-REDACTION: nothing money-shaped renders here (the sweep scans this
 * tab).
 */
import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import {
  confirmSignatureUpload,
  getClinicianProfile,
  requestPhotoUpload,
  requestSignatureUpload,
  updateMyClinicianProfile,
  uploadProfilePhoto,
  uploadSignaturePng,
} from '../api/identity';
import type { ClinicianProfile, ClinicianProfileUpdate } from '../api/types';
import { Row, SectionTitle } from '../staff/ui';
import { Alert, Badge, Button, Card, ErrorLine, ErrorState, Field, Help, Spinner, TextArea, TextInput } from '../ui/kit';
import { SignaturePad } from '../ui/SignaturePad';
import { setProfilePhotoUrl } from './profilePhoto';

type LoadStatus = 'loading' | 'ready' | 'hidden' | 'error';

/** ≤600 chars of bio (mirrors identity's post-sanitisation cap). */
const BIO_MAX = 600;

/**
 * The registered body FOLLOWS from the prescriber type (Wave 8 §1) — it is
 * display-derived, never a separately-stored field: doctor → GMC, pharmacist
 * → GPhC, independent prescriber → no single body (their registration line
 * says it all). Null/unknown → null (row hidden).
 */
export function registeredBodyFor(prescriberType: ClinicianProfile['prescriberType']): string | null {
  switch (prescriberType) {
    case 'doctor':
      return 'GMC — General Medical Council';
    case 'pharmacist':
      return 'GPhC — General Pharmaceutical Council';
    case 'independent_prescriber':
      return 'Independent prescriber';
    default:
      return null;
  }
}

/** Split the derived "First Last" back into editable halves (first space wins). */
function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  const space = trimmed.indexOf(' ');
  if (space < 0) return { firstName: trimmed, lastName: '' };
  return { firstName: trimmed.slice(0, space), lastName: trimmed.slice(space + 1) };
}

const avatarStyle: CSSProperties = {
  width: '72px',
  height: '72px',
  flex: 'none',
  borderRadius: 'var(--gps-r-pill)',
  background: 'var(--gps-primary)',
  color: 'var(--gps-on-accent)',
  display: 'grid',
  placeItems: 'center',
  fontSize: '28px',
  fontWeight: 700,
  overflow: 'hidden',
};

/** The avatar circle — the photo when a signed URL is available, the initial otherwise. */
function Avatar({ profile }: { profile: ClinicianProfile }) {
  const initial = (profile.fullName || '•').charAt(0).toUpperCase();
  return (
    <span aria-hidden="true" style={avatarStyle}>
      {profile.photoUrl ? (
        <img src={profile.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initial
      )}
    </span>
  );
}

export function PrescriptionProfile() {
  const [profile, setProfile] = useState<ClinicianProfile | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [editing, setEditing] = useState(false);
  const [savedNote, setSavedNote] = useState(false);

  const load = useCallback(() => {
    setStatus('loading');
    setProfile(null);
    getClinicianProfile()
      .then((data) => {
        setProfile(data);
        setStatus('ready');
        // Seed the account-menu avatar thumbnail (Wave 8 §1).
        setProfilePhotoUrl(data.photoUrl ?? null);
      })
      .catch((err: unknown) => {
        // No clinician record for this caller — nothing to show, not an error.
        if (err instanceof ApiError && err.status === 404) {
          setStatus('hidden');
          setProfilePhotoUrl(null);
          return;
        }
        setStatus('error');
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (status === 'loading') {
    return (
      <Card>
        <Spinner label="Loading your profile…" />
      </Card>
    );
  }

  if (status === 'hidden') return null;

  if (status === 'error' || !profile) {
    return (
      <Card>
        <ErrorState message="Sorry — we could not load your profile." onRetry={load} />
      </Card>
    );
  }

  function applySaved(refreshed: ClinicianProfile) {
    setProfile(refreshed);
    setEditing(false);
    setSavedNote(true);
    // The header thumbnail follows the save immediately.
    setProfilePhotoUrl(refreshed.photoUrl ?? null);
  }

  return (
    <Card>
      <SectionTitle>Your profile</SectionTitle>
      {savedNote && !editing ? (
        <Alert tone="success" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          Profile saved.
        </Alert>
      ) : null}
      {editing ? (
        <ProfileEditForm profile={profile} onCancel={() => setEditing(false)} onSaved={applySaved} />
      ) : (
        <ProfileView
          profile={profile}
          onEdit={() => {
            setSavedNote(false);
            setEditing(true);
          }}
        />
      )}
      <SignatureSection profile={profile} onProfile={setProfile} />
    </Card>
  );
}

/** VIEW mode — the LinkedIn-style read layout with the single "Edit profile" unlock. */
function ProfileView({ profile, onEdit }: { profile: ClinicianProfile; onEdit: () => void }) {
  const registeredBody = registeredBodyFor(profile.prescriberType);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-4)', padding: '6px 0 12px' }}>
        <Avatar profile={profile} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--gps-text)' }}>{profile.fullName}</div>
          {profile.designation ? (
            <div style={{ fontSize: '14px', color: 'var(--gps-muted)' }}>{profile.designation}</div>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={onEdit}
          style={{ marginLeft: 'auto', width: 'auto', minHeight: '40px', padding: '8px 16px', fontSize: '13.5px' }}
        >
          Edit profile
        </Button>
      </div>

      {registeredBody ? <Row label="Registered body" value={registeredBody} /> : null}
      <Row label="Registration number" value={profile.registrationNumber} />
      <Help style={{ margin: 'var(--gps-s-2) 0 0', fontSize: '12.5px' }}>
        Contact an admin to change your registration details.
      </Help>

      {profile.bio ? (
        <div style={{ padding: '10px 0 4px' }}>
          <div style={{ fontSize: '12.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gps-muted)' }}>
            About
          </div>
          <p style={{ margin: '6px 0 0', fontSize: '14.5px', lineHeight: 1.55, color: 'var(--gps-text)', whiteSpace: 'pre-line' }}>
            {profile.bio}
          </p>
        </div>
      ) : (
        <Help style={{ margin: 'var(--gps-s-2) 0 0' }}>No bio yet — use Edit profile to add a short introduction.</Help>
      )}
    </div>
  );
}

/**
 * EDIT mode — every self-serve field unlocked inline (names, designation,
 * bio, photo); the prescribing identity stays read-only text. ONE Save.
 */
function ProfileEditForm({
  profile,
  onCancel,
  onSaved,
}: {
  profile: ClinicianProfile;
  onCancel: () => void;
  onSaved: (profile: ClinicianProfile) => void;
}) {
  const initialNames = splitFullName(profile.fullName);
  const [firstName, setFirstName] = useState(initialNames.firstName);
  const [lastName, setLastName] = useState(initialNames.lastName);
  const [designation, setDesignation] = useState(profile.designation ?? '');
  const [bio, setBio] = useState(profile.bio ?? '');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoStubNote, setPhotoStubNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = firstName.trim() !== '' && lastName.trim() !== '' && !busy;

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setError(null);
    setPhotoStubNote(false);
    try {
      // 1. The photo rides first: grant → PUT → the PATCH confirms via
      //    photoRef (identity only stamps after a 2xx PUT). A stub grant means
      //    storage isn't configured — save the text fields, note the photo.
      let photoRef: string | undefined;
      if (photoFile) {
        const contentType = photoFile.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const grant = await requestPhotoUpload(contentType);
        if (grant.stub || !grant.objectRef) {
          setPhotoStubNote(true);
        } else {
          try {
            await uploadProfilePhoto(grant, photoFile, contentType);
          } catch {
            setError('Sorry — the photo could not be uploaded. Your other changes were not saved; please try again.');
            return;
          }
          photoRef = grant.objectRef;
        }
      }

      // 2. ONE PATCH carries every edited field (empty designation/bio clear).
      const body: ClinicianProfileUpdate = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        designation: designation.trim() === '' ? null : designation.trim(),
        bio: bio.trim() === '' ? null : bio.trim(),
        ...(photoRef ? { photoRef } : {}),
      };
      const refreshed = await updateMyClinicianProfile(body);
      onSaved(refreshed);
    } catch {
      setError('Sorry — we could not save your profile. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSave} aria-label="Edit profile">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-4)', padding: '6px 0 4px' }}>
        <Avatar profile={profile} />
        <div style={{ flex: 1 }}>
          <Field label="Profile photo" htmlFor="profile-photo" hint="JPEG or PNG — it uploads when you save.">
            <input
              id="profile-photo"
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
              style={{ display: 'block', fontSize: '13.5px' }}
            />
          </Field>
        </div>
      </div>
      {photoFile ? (
        <Help style={{ margin: '0 0 var(--gps-s-2)', fontSize: '12.5px' }}>
          {photoFile.name} will upload when you save.
        </Help>
      ) : null}
      {photoStubNote ? (
        <Alert tone="info" role="status" style={{ margin: 'var(--gps-s-2) 0' }}>
          Photo storage isn&rsquo;t configured on this environment yet — your other changes were saved.
        </Alert>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 var(--gps-s-3)' }}>
        <Field label="First name" htmlFor="profile-first-name">
          <TextInput id="profile-first-name" autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name" htmlFor="profile-last-name">
          <TextInput id="profile-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>

      <Field label="Designation" htmlFor="profile-designation" hint="Your role title — e.g. GP, Clinical Pharmacist.">
        <TextInput id="profile-designation" value={designation} onChange={(e) => setDesignation(e.target.value)} maxLength={120} />
      </Field>

      <Field
        label="Bio"
        htmlFor="profile-bio"
        hint={`A short introduction patients and colleagues see (${String(BIO_MAX - bio.length)} characters left).`}
      >
        <TextArea id="profile-bio" rows={5} value={bio} maxLength={BIO_MAX} onChange={(e) => setBio(e.target.value)} />
      </Field>

      {/* Prescribing identity — deliberately NOT unlocked (admin-gated, G2). */}
      {registeredBodyFor(profile.prescriberType) ? (
        <Row label="Registered body" value={registeredBodyFor(profile.prescriberType)} />
      ) : null}
      <Row label="Registration number" value={profile.registrationNumber} />
      <Help style={{ margin: 'var(--gps-s-2) 0', fontSize: '12.5px' }}>
        Contact an admin to change your registration details.
      </Help>

      {error ? <ErrorLine>{error}</ErrorLine> : null}
      <div style={{ display: 'flex', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
        <Button type="submit" variant="primary" disabled={!canSave} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14px' }}>
          {busy ? 'Saving…' : 'Save profile'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14px' }}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * The signature block — the image inline (signed read) with a SMALL Replace
 * control, or "No signature on file" + the pad. Grant → PNG PUT → confirm
 * (three distinct failure surfaces, TA-06); confirm returns the refreshed
 * profile.
 */
function SignatureSection({
  profile,
  onProfile,
}: {
  profile: ClinicianProfile;
  onProfile: (profile: ClinicianProfile) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [stubNote, setStubNote] = useState(false);
  const [savedNote, setSavedNote] = useState(false);
  // The pad hides behind the small "Replace" control once one is on file.
  const [replacing, setReplacing] = useState(false);

  async function handleSave(blob: Blob) {
    setSaving(true);
    setSaveError(null);
    setSavedNote(false);
    try {
      const grant = await requestSignatureUpload();
      if (grant.stub || !grant.objectRef) {
        // No object storage on this environment — nothing was uploaded.
        setStubNote(true);
        return;
      }
      setStubNote(false);
      try {
        await uploadSignaturePng(grant, blob);
      } catch {
        setSaveError('Sorry — the signature image could not be uploaded. Please try again.');
        return;
      }
      // The PUT succeeded — bind the object to the clinician record. The
      // response is the refreshed profile (signatureSet true + a fresh
      // signed URL for the inline image).
      try {
        const refreshed = await confirmSignatureUpload(grant.objectRef);
        onProfile(refreshed);
        setSavedNote(true);
        setReplacing(false);
      } catch {
        setSaveError('Sorry — the signature was uploaded but could not be confirmed. Please try again.');
      }
    } catch {
      // Only the grant request can reach here (PUT/confirm handle their own).
      setSaveError('Sorry — we could not start the signature upload. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const showPad = !profile.signatureSet || replacing;

  return (
    <div style={{ marginTop: 'var(--gps-s-4)', borderTop: '1px solid var(--gps-border)', paddingTop: 'var(--gps-s-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--gps-s-3)' }}>
        <span style={{ fontSize: '14.5px', fontWeight: 600, color: 'var(--gps-text)' }}>Signature</span>
        {profile.signatureSet && !replacing ? (
          // Wave 8: the giant primary button is gone — a SMALL ghost control.
          <Button
            type="button"
            variant="ghost"
            disabled={saving}
            onClick={() => {
              setReplacing(true);
              setSavedNote(false);
            }}
            style={{ width: 'auto', minHeight: '36px', padding: '6px 14px', fontSize: '13px' }}
          >
            Replace
          </Button>
        ) : null}
      </div>

      {profile.signatureSet ? (
        profile.signatureUrl ? (
          <img
            src={profile.signatureUrl}
            alt="Your signature as it appears on prescriptions"
            style={{
              display: 'block',
              maxWidth: '260px',
              maxHeight: '96px',
              marginTop: 'var(--gps-s-2)',
              border: '1px solid var(--gps-border)',
              borderRadius: 'var(--gps-r-md)',
              background: '#fff',
              padding: '6px',
            }}
          />
        ) : (
          <Help style={{ margin: 'var(--gps-s-2) 0 0' }}>
            Signature on file — the preview isn&rsquo;t available on this environment.
          </Help>
        )
      ) : (
        <div style={{ marginTop: 'var(--gps-s-2)' }}>
          <Badge tone="pending">No signature on file — required on prescriptions</Badge>
        </div>
      )}

      {stubNote ? (
        <Alert tone="info" role="status" style={{ marginTop: 'var(--gps-s-3)' }}>
          Signature storage isn&rsquo;t configured on this environment yet.
        </Alert>
      ) : null}

      {savedNote ? (
        <Alert tone="success" role="status" style={{ marginTop: 'var(--gps-s-3)' }}>
          Signature saved — it will appear on your prescriptions.
        </Alert>
      ) : null}

      {showPad ? (
        <div style={{ marginTop: 'var(--gps-s-3)' }}>
          <SignaturePad saving={saving} onSave={(blob) => void handleSave(blob)} />
          {replacing ? (
            <Button
              type="button"
              variant="ghost"
              disabled={saving}
              onClick={() => setReplacing(false)}
              style={{ width: 'auto', minHeight: '36px', padding: '6px 14px', fontSize: '13px', marginTop: 'var(--gps-s-2)' }}
            >
              Keep current signature
            </Button>
          ) : null}
        </div>
      ) : null}
      {saveError ? <ErrorLine>{saveError}</ErrorLine> : null}
    </div>
  );
}
